import React, { useMemo } from 'react';
import moment from '../../../../moment';
import { ProgressCard, ProgressResultValue, ProgressResultValueDefinition } from './types';
import {
	AccessorColumnDef,
	AccessorKeyColumnDef,
	CellContext,
	createColumnHelper,
} from '@tanstack/react-table';
import { compareDates } from '../../../../utils/sort';
import Table from './Table';

export type ProgressCardDataEntry =
	| {
			label: string;
			position: number;
			results: undefined;
			progressResultValueDefinition: undefined;
	  }
	| {
			label: string;
			progressResultValueDefinition: ProgressResultValueDefinition;
			results: Record<string, ProgressResultValue>;
	  };

const columnHelper = createColumnHelper<ProgressCardDataEntry>();
const ProgressOverview = ({ progressCardData }: { progressCardData: ProgressCard }) => {
	const tableRows = useMemo(() => {
		if (progressCardData?.template && progressCardData?.resultSets) {
			const rows = {} as any;
			progressCardData.template.activePhases.forEach((phase) => {
				phase.activeSteps.forEach((step) => {
					rows[step.id] = {
						label: step.name,
						progressResultValueDefinition: step.progressResultValueDefinition,
						results: {},
					};
				});
			});

			const results = {} as any;
			progressCardData.resultSets.forEach((resultSet) => {
				const dateKey = moment(resultSet.attendance.start).format('DD-MM-YYYY');
				results[dateKey] = {};
				resultSet.results.forEach((result) => {
					results[dateKey][result.progressStep.id] = result.progressResultValue;
				});
			});

			Object.keys(results).forEach((dateKey) => {
				const values = results[dateKey];
				Object.keys(values).forEach((stepId) => {
					if (rows[stepId]) {
						rows[stepId].results[dateKey] = values[stepId];
					}
				});
			});

			const result = [] as any[];
			progressCardData.template.activePhases.forEach((phase) => {
				result.push({
					label: phase.name,
					position: phase.position,
				});

				phase.activeSteps.forEach((step) => {
					result.push(rows[step.id]);
				});
			});

			return result;
		}
		return [];
	}, [progressCardData]);

	const columns = useMemo(() => {
		if (!progressCardData.resultSets) {
			return [];
		}
		return [
			columnHelper.accessor('label', {
				header: undefined,
				minSize: 200,
				size: 200,
				maxSize: 200,
				enablePinning: true,
			}),
			...progressCardData.resultSets
				.sort((a, b) => compareDates(moment(a.attendance.start), moment(b.attendance.start)))
				.map((resultSet) => {
					return {
						header: () => (
							<span className={'border-start px-2 text-center'}>
								{moment(resultSet.attendance.start).format('DD-MM')}
							</span>
						),
						accessorFn: (row) => {
							const dateKey = moment(resultSet.attendance.start).format('DD-MM-YYYY');
							return row.results && row.results[dateKey] ? row.results[dateKey] : row.label;
						},
						id: resultSet.attendance.id.toString(),
						cell: ProgressCell,
						minSize: 60,
						size: 60,
						maxSize: 60,
					} as AccessorColumnDef<ProgressCardDataEntry, unknown>;
				}),
		] as AccessorKeyColumnDef<ProgressCardDataEntry, unknown>[];
	}, [progressCardData]);

	return <Table data={tableRows} columns={columns} />;
};

export default ProgressOverview;

const ProgressCell = (cellInfo: CellContext<ProgressCardDataEntry, ProgressResultValue>) => {
	const value = cellInfo.cell.getValue();
	return <div style={{ backgroundColor: value?.color, textAlign: 'center' }}>{value.value}</div>;
};
